import melodyStyleData from '@/data/melodyStyles.json';
import packMap from '@/data/packMap.json';
import { normalizeKey } from './chords';

export const SCALE_INTERVALS = Object.freeze({
  'natural minor': [0, 2, 3, 5, 7, 8, 10],
  'harmonic minor': [0, 2, 3, 5, 7, 8, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  'minor pentatonic': [0, 3, 5, 7, 10],
  major: [0, 2, 4, 5, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  'major pentatonic': [0, 2, 4, 7, 9],
});

const MINOR_SCALES = new Set(['natural minor', 'harmonic minor', 'dorian', 'phrygian', 'minor pentatonic']);
const scaleMode = (scale) => MINOR_SCALES.has(scale) ? 'minor' : 'major';
const mod = (value, base = 12) => ((value % base) + base) % base;

export function resolveStarterScale({ packId = null, family = 'trap', keySignature = 'C min' } = {}) {
  const session = normalizeKey(keySignature) || normalizeKey('C min');
  const pack = (packMap.packs || []).find((item) => item.id === packId);
  let name = String(pack?.key?.scale || melodyStyleData.families?.[family]?.scale || (session.mode === 'major' ? 'major' : 'natural minor')).toLowerCase();
  if (!SCALE_INTERVALS[name] || scaleMode(name) !== session.mode) name = session.mode === 'major' ? 'major' : 'natural minor';
  return { name, root: session.root, mode: session.mode, intervals: SCALE_INTERVALS[name], pitchClasses: SCALE_INTERVALS[name].map((interval) => mod(session.root + interval)) };
}

export function scalePitches(scale, low = 0, high = 127) {
  const classes = new Set(scale.pitchClasses);
  const pitches = [];
  for (let pitch = low; pitch <= high; pitch += 1) if (classes.has(mod(pitch))) pitches.push(pitch);
  return pitches;
}

export function passTone(previous, next, scale, low = 60, high = 79) {
  const pitches = scalePitches(scale, low, high);
  const between = pitches.filter((pitch) => pitch > Math.min(previous, next) && pitch < Math.max(previous, next));
  if (between.length) {
    const midpoint = (previous + next) / 2;
    return [...between].sort((a, b) => Math.abs(a - midpoint) - Math.abs(b - midpoint) || a - b)[0];
  }
  return pitches.find((pitch) => pitch > previous) ?? pitches[0] ?? Math.min(high, previous + 1);
}

export function approachTone(nextRootPitchClass, currentPitch, scale, low, high) {
  const pitches = scalePitches(scale, low, high);
  const roots = Array.from({ length: high - low + 25 }, (_, index) => low - 12 + index).filter((pitch) => mod(pitch) === mod(nextRootPitchClass));
  const choices = roots.flatMap((root) => {
    const below = [...pitches].reverse().find((pitch) => pitch < root);
    const above = pitches.find((pitch) => pitch > root);
    return [below, above].filter(Number.isFinite);
  });
  return [...new Set(choices)].sort((a, b) => Math.abs(a - currentPitch) - Math.abs(b - currentPitch) || a - b)[0] ?? currentPitch;
}
